// Candy drops — decorative Halloween sprites that drift down the play
// area during October daily runs. Purely visual (no gameplay impact).
// Consumer pattern:
//   maybeSpawnCandy(candiesRef, W, nowMs);
//   tickAndDrawCandy(ctx, candiesRef, dtMs, floorY, nowMs);

const SPAWN_INTERVAL_MS = 900;
const GRAVITY = 0.00018;
const DRIFT = 0.02;

// Named palette + emoji-style glyphs so each drop reads as a distinct
// treat (wrapped candy, lollipop stick, pumpkin, candy corn).
const KINDS = [
  { fill: "#ff4fa3", edge: "#ffd4e6", stripe: "#ffffff", kind: "wrapper" },
  { fill: "#ff8a00", edge: "#ffcc66", stripe: "#3d8033", kind: "pumpkin" },
  { fill: "#c084fc", edge: "#e0c8ff", stripe: "#7c3aed", kind: "wrapper" },
  { fill: "#ffea3a", edge: "#fff3a0", stripe: "#ff6a00", kind: "candycorn" },
];

function pickKind() {
  return KINDS[Math.floor(Math.random() * KINDS.length)];
}

export function maybeSpawnCandy(candiesRef, canvasW, nowMs) {
  const list = candiesRef.current;
  if (!list) return;
  const last = candiesRef.lastSpawnMs || 0;
  if (nowMs - last < SPAWN_INTERVAL_MS) return;
  candiesRef.lastSpawnMs = nowMs;
  list.push({
    x: Math.random() * canvasW,
    y: -14,
    vx: (Math.random() - 0.5) * DRIFT * 40,
    vy: 0.16 + Math.random() * 0.18,
    rot: Math.random() * Math.PI * 2,
    vrot: (Math.random() - 0.5) * 0.004,
    r: 6 + Math.random() * 3,
    kind: pickKind(),
    bornAt: nowMs,
  });
}

function drawWrapper(ctx, k, r) {
  // Twist-wrapped candy — oval body with side twists.
  ctx.fillStyle = k.fill;
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.62, 0, 0, Math.PI * 2);
  ctx.fill();
  // stripe
  ctx.strokeStyle = k.stripe;
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, 0);
  ctx.lineTo(r * 0.6, 0);
  ctx.stroke();
  // twists
  ctx.fillStyle = k.edge;
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.lineTo(-r - r * 0.55, -r * 0.4);
  ctx.lineTo(-r - r * 0.55, r * 0.4);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(r + r * 0.55, -r * 0.4);
  ctx.lineTo(r + r * 0.55, r * 0.4);
  ctx.closePath();
  ctx.fill();
}

function drawPumpkinDrop(ctx, r) {
  // Small kawaii pumpkin
  ctx.fillStyle = "#ff8a00";
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffcc66";
  ctx.beginPath();
  ctx.arc(-r * 0.35, -r * 0.2, r * 0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#3d8033";
  ctx.fillRect(-r * 0.18, -r * 1.15, r * 0.36, r * 0.4);
  ctx.fillStyle = "#1a0008";
  ctx.beginPath();
  ctx.arc(-r * 0.3, r * 0.05, r * 0.13, 0, Math.PI * 2);
  ctx.arc(r * 0.3, r * 0.05, r * 0.13, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#1a0008";
  ctx.lineWidth = Math.max(0.7, r * 0.1);
  ctx.beginPath();
  ctx.arc(0, r * 0.28, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI);
  ctx.stroke();
}

function drawCandyCorn(ctx, r) {
  // Three-stripe triangle candy corn
  ctx.fillStyle = "#ffea3a";
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(-r * 0.7, r);
  ctx.lineTo(r * 0.7, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#ff8a00";
  ctx.beginPath();
  ctx.moveTo(-r * 0.45, r * 0.1);
  ctx.lineTo(r * 0.45, r * 0.1);
  ctx.lineTo(r * 0.6, r * 0.55);
  ctx.lineTo(-r * 0.6, r * 0.55);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(-r * 0.22, -r * 0.35);
  ctx.lineTo(r * 0.22, -r * 0.35);
  ctx.closePath();
  ctx.fill();
}

export function tickAndDrawCandy(ctx, candiesRef, dtMs, floorY, nowMs) {
  const list = candiesRef.current;
  if (!list || !list.length) return;
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i];
    p.vy += GRAVITY * dtMs;
    p.x += p.vx * dtMs * 0.06;
    p.y += p.vy * dtMs * 0.06;
    p.rot += p.vrot * dtMs;
    // Reap when off-floor OR older than 15s (safety)
    if (p.y > floorY || nowMs - p.bornAt > 15000) {
      list.splice(i, 1);
      continue;
    }
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.globalAlpha = 0.85;
    ctx.shadowColor = p.kind.fill;
    ctx.shadowBlur = 6;
    if (p.kind.kind === "pumpkin") drawPumpkinDrop(ctx, p.r);
    else if (p.kind.kind === "candycorn") drawCandyCorn(ctx, p.r);
    else drawWrapper(ctx, p.kind, p.r);
    ctx.restore();
  }
}

export function clearCandy(candiesRef) {
  if (candiesRef.current) candiesRef.current.length = 0;
  candiesRef.lastSpawnMs = 0;
}
